import colors from '@utils/colors';

// The app's green, white, and a paler green between them.
const SPARKLE_COLORS = [colors.positive, colors.white, `${colors.positive}99`];

export type SparkleOptions = {
  /** When to burst, in ms from now. */
  delay?: number;
  /** Which way: radians, 0 = right, negative = up. All round when left out. */
  aim?: number;
  /** How wide a fan, in radians, around `aim`. */
  spread?: number;
  /** How far they fly, least and most, in px. */
  reach?: [number, number];
  duration?: number;
  count?: number;
  /** Short streaks pointing the way they fly, like a shot, rather than round sparkles drifting down. */
  streaks?: boolean;
  /** Fire from a line this long through the point, across the way they fly, rather than from the point alone. */
  along?: number;
};

/**
 * A burst of sparkles from a point on screen, flying out and fading: a completed task's checkbox in the Task List as
 * the green sweeping its row arrives, the session popup's Mark Task Complete, and a shot out of a sidebar card's side
 * (Petr: "more exciting and powerful and fun"; a first, smaller burst at a row's far end went unnoticed, "I don't see
 * any"). Booked up front and run by the browser, so a busy page doesn't stall it; nothing for a student who asks for
 * less motion.
 */
export const sparkleBurst = (x: number, y: number, options: SparkleOptions = {}) => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const {
    delay = 0,
    aim,
    spread = 2 * Math.PI,
    reach = [35, 75],
    duration = 750,
    count = 16,
    streaks = false,
    along = 0
  } = options;
  // Across the way they fly: where along the line each one starts.
  const across = (aim ?? 0) + Math.PI / 2;
  for (let i = 0; i < count; i++) {
    const spark = document.createElement('div');
    const size = 6 + (i % 4) * 2;
    const [width, height] = streaks ? [size + 4, 3] : [size, size];
    const offset = along ? ((i + 0.5) / count - 0.5) * along : 0;
    const startX = x + Math.cos(across) * offset;
    const startY = y + Math.sin(across) * offset;
    Object.assign(spark.style, {
      position: 'fixed',
      left: `${startX - (streaks ? 0 : width / 2)}px`,
      top: `${startY - height / 2}px`,
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: streaks ? '2px' : i % 3 === 0 ? '2px' : '50%',
      background: SPARKLE_COLORS[i % SPARKLE_COLORS.length],
      boxShadow: `0 0 8px ${colors.positive}`,
      transformOrigin: streaks ? 'left center' : 'center',
      pointerEvents: 'none',
      zIndex: '10000',
      willChange: 'transform, opacity',
      // Unseen until its turn, and after it.
      opacity: '0'
    });
    document.body.appendChild(spark);
    if (typeof spark.animate !== 'function') {
      spark.remove();
      continue;
    }
    // Spread evenly over the fan (all round by default), a little uneven.
    const angle =
      aim === undefined
        ? (2 * Math.PI * i) / count + (Math.random() - 0.5) * 0.5
        : aim - spread / 2 + (spread * (i + 0.5)) / count + (Math.random() - 0.5) * (spread / count);
    const distance = reach[0] + Math.random() * (reach[1] - reach[0]);
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const keyframes: Keyframe[] = streaks
      ? // Shot straight out along their line, stretching as they go and shrinking away.
        [
          { transform: `translate(0, 0) rotate(${angle}rad) scaleX(0.2)`, opacity: 1 },
          {
            transform: `translate(${dx * 0.55}px, ${dy * 0.55}px) rotate(${angle}rad) scaleX(1.3)`,
            opacity: 1,
            offset: 0.4
          },
          { transform: `translate(${dx}px, ${dy}px) rotate(${angle}rad) scaleX(0.2)`, opacity: 0 }
        ]
      : // Out, spinning, and drifting down as they fade.
        [
          { transform: 'translate(0, 0) scale(0.3) rotate(0deg)', opacity: 1 },
          { transform: `translate(${dx * 0.75}px, ${dy * 0.75}px) scale(1.3) rotate(120deg)`, opacity: 1, offset: 0.35 },
          { transform: `translate(${dx}px, ${dy + 14}px) scale(0) rotate(240deg)`, opacity: 0 }
        ];
    spark
      .animate(keyframes, { duration, delay, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' })
      .addEventListener('finish', () => spark.remove());
  }
};
